/**
 * Structured Data (Schema.org) Generators
 * Creates JSON-LD structured data for better SEO
 */

import { GEO_HOME_FAQ } from './geoFaqContent';
import { BUSINESS_ID, DEFAULT_SITE_URL } from './siteUrl';

const siteUrl = DEFAULT_SITE_URL;

const SERVED_CITIES = ['Kochi', 'Thiruvananthapuram', 'Calicut'];

/**
 * Organization Schema
 */
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': BUSINESS_ID,
  name: 'Fixome',
  url: siteUrl,
  logo: `${siteUrl}/logo_transparent.png`,
  image: `${siteUrl}/logo_transparent.png`,
  description: 'Professional home maintenance platform providing reliable electrical repairs, AC servicing, plumbing solutions, painting, pest control, and deep cleaning for homes and commercial spaces. Quick, professional, and hassle-free services through skilled technicians.',
  telephone: '+91-70126-89824',
  email: 'support@fixome.in',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '1st Floor Vaishnavam, Kailas Nagar Puthiya Road, Pallinada, Chakkalakkal, Palarivattom',
    addressLocality: 'Kochi',
    addressRegion: 'Kerala',
    postalCode: '682025',
    addressCountry: 'IN',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '9.9922746',
    longitude: '76.3130916',
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '08:00',
      closes: '19:00',
    },
  ],
  priceRange: '₹₹',
  contactPoint: [
    {
      '@type': 'ContactPoint',
      telephone: '+91-70126-89824',
      contactType: 'customer support',
      areaServed: 'IN',
      availableLanguage: ['en', 'ml'],
    },
  ],
  areaServed: {
    '@type': 'State',
    name: 'Kerala',
    containedIn: {
      '@type': 'Country',
      name: 'India',
    },
  },
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Home Services',
    itemListElement: [
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Plumbing Services',
          description: 'Professional plumbing repairs and installations',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Electrical Services',
          description: 'Expert electrical repairs and installations',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'AC Repair & Servicing',
          description: 'Air conditioning repair and maintenance',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Painting Services',
          description: 'Professional interior and exterior painting',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Pest Control',
          description: 'Effective pest control and prevention',
        },
      },
      {
        '@type': 'Offer',
        itemOffered: {
          '@type': 'Service',
          name: 'Deep Cleaning',
          description: 'Comprehensive home and office cleaning',
        },
      },
    ],
  },
  sameAs: [
    'https://www.facebook.com/share/1ATQpi6hun/?mibextid=wwXIfr',
    'https://www.instagram.com/fixome.in',
  ],
});

/**
 * Website Schema
 */
export const getWebsiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${siteUrl}/#website`,
  url: siteUrl,
  name: 'Fixome',
  description:
    'Book verified plumbers, electricians, AC technicians, cleaners, and more in Kochi, Thiruvananthapuram, and Calicut. Online booking, transparent booking fees, 8am–7pm service.',
  publisher: {
    '@id': BUSINESS_ID,
  },
});

/**
 * Service Schema
 */
export const getServiceSchema = (service) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  '@id': `${siteUrl}/services/${service.id}#service`,
  name: service.name,
  description: service.description,
  provider: {
    '@id': BUSINESS_ID,
  },
  areaServed: {
    '@type': 'City',
    name: service.city || 'Your City',
  },
  serviceType: service.category,
  category: service.category,
  image: service.icon || `${siteUrl}${service.imageUrl}`,
  offers: {
    '@type': 'Offer',
    price: service.basePrice,
    priceCurrency: 'INR',
    availability: 'https://schema.org/InStock',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: service.basePrice,
      priceCurrency: 'INR',
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        value: '1',
        unitText: 'service call',
      },
    },
  },
});

/**
 * Breadcrumb Schema
 */
export const getBreadcrumbSchema = (breadcrumbs) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: crumb.name,
    item: crumb.url.startsWith('http') ? crumb.url : `${siteUrl}${crumb.url}`,
  })),
});

/**
 * Review Schema
 */
export const getReviewSchema = (review) => ({
  '@context': 'https://schema.org',
  '@type': 'Review',
  itemReviewed: {
    '@type': 'Service',
    name: review.serviceName,
  },
  author: {
    '@type': 'Person',
    name: review.customerName,
  },
  reviewRating: {
    '@type': 'Rating',
    ratingValue: review.rating,
    bestRating: '5',
    worstRating: '1',
  },
  reviewBody: review.comment,
  datePublished: review.createdAt,
});

/**
 * FAQ Schema
 */
export const getFAQSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

/**
 * Local Business with Service Area
 */
export const getLocalBusinessSchema = (locations = []) => ({
  '@context': 'https://schema.org',
  '@type': 'HomeAndConstructionBusiness',
  '@id': BUSINESS_ID,
  name: 'Fixome',
  image: `${siteUrl}/logo_transparent.png`,
  telephone: '+91-70126-89824',
  email: 'support@fixome.in',
  url: siteUrl,
  sameAs: [
    'https://www.facebook.com/share/1ATQpi6hun/?mibextid=wwXIfr',
    'https://www.instagram.com/fixome.in',
  ],
  areaServed: (locations.length > 0 ? locations.map((loc) => loc.city) : SERVED_CITIES).map((name) => ({
    '@type': 'City',
    name,
  })),
});

/**
 * Offer Schema (for special deals)
 */
export const getOfferSchema = (offer) => ({
  '@context': 'https://schema.org',
  '@type': 'Offer',
  name: offer.title,
  description: offer.description,
  price: offer.discountedPrice,
  priceCurrency: 'INR',
  priceValidUntil: offer.validUntil,
  availability: 'https://schema.org/InStock',
  seller: {
    '@id': BUSINESS_ID,
  },
  validFrom: offer.validFrom,
});

/**
 * BlogPosting schema for article pages
 */
export const getBlogPostingSchema = (blog, slug) => {
  const pageUrl = `${siteUrl}/blogs/${slug}`;
  const published = blog.publishedAt ? new Date(blog.publishedAt).toISOString() : undefined;
  const modified = blog.updatedAt
    ? new Date(blog.updatedAt).toISOString()
    : published;

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blog.title,
    description: blog.description,
    image: blog.coverImageUrl ? [blog.coverImageUrl] : [`${siteUrl}/fixome_logo.png`],
    datePublished: published,
    dateModified: modified || published,
    author: {
      '@type': 'Organization',
      name: 'Fixome',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Fixome',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/fixome_logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
  };
};

/**
 * BlogPosting plus FAQPage in one @graph when the post has a visible FAQ.
 * Without FAQs this returns the plain BlogPosting object, unchanged.
 */
export const getBlogStructuredData = (blog, slug, faqs) => {
  const posting = getBlogPostingSchema(blog, slug);
  if (!Array.isArray(faqs) || faqs.length === 0) return posting;
  const { '@context': context, ...postingNode } = posting;
  return {
    '@context': context,
    '@graph': [
      postingNode,
      {
        '@type': 'FAQPage',
        '@id': `${siteUrl}/blogs/${slug}#faq`,
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: { '@type': 'Answer', text: faq.answer },
        })),
      },
    ],
  };
};

/**
 * FAQPage node for @graph (homepage): matches visible FAQ on HomePage + llms.txt themes.
 */
const getHomeFaqPageNode = () => ({
  '@type': 'FAQPage',
  '@id': `${siteUrl}/#homepage-faq`,
  mainEntity: GEO_HOME_FAQ.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

/**
 * Homepage JSON-LD: one HomeAndConstructionBusiness, plus the FAQ that
 * matches the visible homepage questions. No GeoCircle, hasMap, or SearchAction.
 */
export const getHomePageSchema = () => {
  const business = getOrganizationSchema();
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'HomeAndConstructionBusiness',
        '@id': BUSINESS_ID,
        name: business.name,
        url: business.url,
        logo: business.logo,
        image: business.image,
        description: business.description,
        telephone: business.telephone,
        email: business.email,
        address: business.address,
        openingHoursSpecification: business.openingHoursSpecification,
        sameAs: business.sameAs,
        areaServed: SERVED_CITIES.map((name) => ({
          '@type': 'City',
          name,
        })),
      },
      getHomeFaqPageNode(),
    ],
  };
};

